import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Anchor, Wine, Music, MapPin, Clock, ChevronRight, Star, ArrowRight, Phone, Check, X, Crown, Users, GlassWater, Camera, Sailboat, Target, AlertCircle, Wand2 } from "lucide-react";
import SEOHead from "../components/SEOHead";
import Starfield from "../components/Starfield";
import { MAGIC_PHOTOS, CONTACT } from "../data/cruiseData";
import DateAvailabilityChecker from "../components/DateAvailabilityChecker";

const API = process.env.REACT_APP_BACKEND_URL;

/* ───────────────────────────────────────────────────────────────────────────
   BYO CRUISE SYDNEY HARBOUR, Private full-boat charter, bring your own
   Two packages from the live magiccruises.com.au/byo-cruise-sydney/ page
   ─────────────────────────────────────────────────────────────────────────── */

const ORANGE = "#FB923C";
const ROSE = "#E11D48";

const HIGHLIGHTS = [
 { icon: Anchor, label: "PRIVATE CHARTER", sub: "Full boat exclusive use" },
 { icon: GlassWater, label: "BYO FOOD & DRINKS", sub: "Or food only · your call" },
 { icon: Users, label: "UP TO 75 GUESTS", sub: "Two flexible packages" },
 { icon: Clock, label: "3.5 HOURS", sub: "Sydney Harbour cruising" }];

const PACKAGES = [
 {
  id: "p1",
  tier: "BYO PACKAGE 1",
  subtitle: "Food + Drinks · 3.5 hour charter",
  badge: "BYO FOOD + DRINKS",
  pricing: [
   { season: "Jan to Oct", price: "$3,600" },
   { season: "Nov to Dec", price: "$4,400" }],
  caption: "Per private charter · max 50 guests · 3.5 hours",
  capacityLabel: "Maximum 50 guests",
  inclusions: [
   "3.5 hour vessel hire (private exclusive use)", "Wharf fees", "Staff fees", "BYO food surcharge", "BYO drinks surcharge", "BYO disposable cutlery & plates", "BYO ice provided"],
  conditions: [
   "Beverages must be arranged as ONE delivery prior to the event", "Individual BYO is strictly not permitted", "BYO spirits & mixed spirit drinks permitted (conditions apply)", "BYO catering must be sufficient for all guests", "Easy serviceable foods recommended (e.g. platter foods)"],
  cta: "Enquire about Package 1",
  highlight: true,
 },
 {
  id: "p2",
  tier: "BYO PACKAGE 2",
  subtitle: "Food + Cash Bar · 3.5 hour charter",
  badge: "BYO FOOD ONLY",
  pricing: [
   { season: "Feb to Oct", price: "$2,800" },
   { season: "Nov to Jan", price: "$3,900" }],
  caption: "Per private charter · max 75 guests · 3.5 hours",
  capacityLabel: "Maximum 75 guests",
  inclusions: [
   "3.5 hour vessel hire (private exclusive use)", "Wharf fees", "BYO food surcharge", "Sound system included"],
  bar: [
   "Individual purchase (cash bar only on board)", "Bar tab option: 1 bar staff required ($275 per staff)", "Pre-paid bar tab available", "Minimum $1,000 bar spend", "No beverage package available with this offer"],
  conditions: [
   "Guests take rubbish off at end of cruise (or +$200 for Magic disposal)", "BYO catering must be sufficient for all guests", "Easy serviceable foods recommended (e.g. platter foods)", "No 21 years or under guests permitted on BYO", "No bucks or hens cruises permitted on BYO packages"],
  cta: "Enquire about Package 2",
  highlight: false,
 }];

const EXTRAS = [
 { Icon: Music, label: "DJ", price: "$550" },
 { Icon: Camera, label: "Photographer", price: "$650" },
 { Icon: Wand2, label: "Magicians", price: "$1,800" },
 { Icon: Sailboat, label: "Jet boat rides", price: "$1,850" },
 { Icon: Target, label: "Clay pigeon shooting", price: "$1,550" }];

const FAQ = [
 { q: "What's the difference between Package 1 and Package 2?", a: "Package 1 lets you BYO both food AND drinks (3.5 hour charter, max 50 guests, $3,600 Jan-Oct / $4,400 Nov-Dec). Package 2 lets you BYO food only with a licensed cash bar on board (3.5 hour charter, max 75 guests, $2,800 Feb-Oct / $3,900 Nov-Jan)." },
 { q: "Can I BYO any food I want?", a: "Yes, bring whatever suits your group. We recommend easy serviceable foods like platters, sandwiches, antipasto boards, cheese boards. Sufficient food must be brought for all guests. Disposable plates, cutlery and ice are included." },
 { q: "Can I BYO alcohol on Package 1?", a: "Yes. All beverages must be arranged as ONE delivery to the boat prior to the event, individual BYO from each guest is strictly not permitted. BYO spirits and mixed spirit drinks are permitted under conditions. Talk to us about your beverage requirements." },
 { q: "Why can't I BYO alcohol on Package 2?", a: "Package 2 is the lower price-point that includes the sound system, instead of BYO drinks, you have access to our licensed bar with cash bar, bar tab, or pre-paid options ($1,000 minimum spend). This works better for groups who don't want to coordinate a single drinks delivery." },
 { q: "Why can't 21-and-under or hens/bucks parties book BYO?", a: "Operational and licensing reasons. Magic accepts hens, bucks and young-adult bookings via our standard catered packages (not BYO). Reach out and we'll match you to the right package." },
 { q: "What about rubbish at the end?", a: "Package 2: guests take their rubbish off the boat at end of cruise, or we can dispose of it for $200. Package 1: included in your package." },
 { q: "Can I add entertainment?", a: "Yes, both packages allow add-on entertainment: DJ ($550), Photographer ($650), Magicians ($1,800), Jet boat rides ($1,850), Clay pigeon shooting ($1,550). All bookable with us as one invoice." }];

const REVIEWS = [
 { name: "Sofia Brichet", role: "Family Celebration", rating: 5, body: "The combination of the Sydney skyline, our own food and BYO drinks made this the most memorable family party we've thrown. The kids were in awe of the views." },
 { name: "James Thomas", role: "Anniversary", rating: 5, body: "BYO meant we could bring the wine my husband and I had been saving for years. The crew made us feel like royalty. Absolutely magical." },
 { name: "David R.", role: "Corporate Function", rating: 5, body: "Hosted a corporate event using Package 2 with a bar tab. Clients were mesmerised by the harbour. The flexibility of BYO catering meant we could bring our own caterer." }];

export default function BYOCruise() {
 useEffect(() => { document.title = "BYO Cruise Sydney Harbour, Private Charter From $2,800 | Magic Cruises"; }, []);

 // Pull live CMS prices/inclusions/rules from admin. Falls back to static
 // PACKAGES if API is unavailable. Seasonal pricing structure preserved ,
 // CMS overrides only the low-season base price + inclusions + conditions.
 const [cmsTiers, setCmsTiers] = useState(null);
 useEffect(() => {
  let cancelled = false;
  fetch(`${API}/api/cms/packages/byo`)
   .then((r) => r.ok ? r.json() : null)
   .then((doc) => { if (!cancelled && doc && doc.packages) setCmsTiers(doc.packages); })
   .catch(() => {});
  return () => { cancelled = true; };
 }, []);

 const livePackages = useMemo(() => {
  if (!cmsTiers) return PACKAGES;
  return PACKAGES.map((p, i) => {
   const cms = cmsTiers[i];
   if (!cms) return p;
   const pricing = Array.isArray(p.pricing) && p.pricing.length
    ? [{ ...p.pricing[0], price: cms.price || p.pricing[0].price }, ...p.pricing.slice(1)]
    : p.pricing;
   return {
    ...p,
    pricing,
    inclusions: cms.inclusions && cms.inclusions.length ? cms.inclusions : p.inclusions,
    conditions: cms.rules && cms.rules.length ? cms.rules : p.conditions,
   };
  });
 }, [cmsTiers]);

 return (
  <main className="bg-[#0F1B2E] text-white overflow-hidden" data-testid="byo-page">
   <SEOHead
    title="BYO Cruise Sydney Harbour, Private Charter From $2,800 | Magic Cruises"
    description="Hire the Magic for a BYO cruise on Sydney Harbour. Two private charter packages, Package 1 (BYO food + drinks, 4hr, max 50, from $3,600) or Package 2 (BYO food + cash bar, 3.5hr, max 75, from $2,800). King St Wharf 3."
    canonical="https://magiccruises.com.au/cruises/byo-cruise-sydney-harbour"
   />

   {/* ═══════════ HERO ═══════════ */}
   <section className="relative overflow-hidden bg-[#0F1B2E] flex flex-col isolate" data-testid="byo-hero">
    <div className="relative w-full min-h-[80vh] flex items-center justify-center px-5 sm:px-8 lg:px-10 pt-28 pb-16 lg:py-24">
     <img src={MAGIC_PHOTOS.byoSunsetDeck} alt="BYO cruise sunset on the rear deck of MV Sydney Magic on Sydney Harbour" className="absolute inset-0 w-full h-full object-cover opacity-55 scale-110" fetchPriority="high" />
     <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(15,27,46,0.85) 0%, rgba(36,24,39,0.55) 30%, rgba(58,30,36,0.55) 60%, rgba(15,27,46,0.95) 100%)" }} />
     <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[120%] h-[55%] opacity-70" style={{ background: "radial-gradient(ellipse at center top, rgba(251,146,60,0.20) 0%, transparent 60%)" }} />
     <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[120%] h-[55%] opacity-60" style={{ background: "radial-gradient(ellipse at center bottom, rgba(225,29,72,0.18) 0%, transparent 65%)" }} />
     <Starfield density={45} color="#FFD6B5" glow="rgba(251,146,60,0.55)" fixed={false} intensity={0.5} />
     <div className="absolute top-[12%] right-[8%] w-[28rem] h-[28rem] rounded-full blur-[120px] pointer-events-none animate-pulse" style={{ animationDuration: "6s", backgroundColor: "rgba(251,146,60,0.20)" }} />
     <div className="absolute bottom-[8%] left-[6%] w-[22rem] h-[22rem] rounded-full blur-[100px] pointer-events-none animate-pulse" style={{ animationDuration: "8s", animationDelay: "2s", backgroundColor: "rgba(225,29,72,0.18)" }} />

     <h1 className="sr-only">BYO Cruise Sydney Harbour, Two private full-boat charter packages aboard Magic.</h1>

     <div className="relative z-10 text-center max-w-5xl mx-auto w-full">
      <div className="inline-flex items-center gap-3 sm:gap-4 mb-5 lg:mb-7 animate-in fade-in slide-in-from-top-4 duration-1000">
       <span className="block w-10 sm:w-14 h-px" style={{ backgroundColor: ORANGE, opacity: 0.8 }} />
       <span className="text-white/95 text-[11px] sm:text-[13px] lg:text-sm uppercase tracking-[0.5em] font-bold">Private full-boat charter</span>
       <span className="block w-10 sm:w-14 h-px" style={{ backgroundColor: ORANGE, opacity: 0.8 }} />
      </div>
      <h2 className="font-black tracking-tight leading-[0.88] animate-in fade-in zoom-in-95 duration-1000" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(3rem, 11vw, 8.5rem)", letterSpacing: "-0.02em" }}>
       <span className="block bg-clip-text text-transparent" style={{ backgroundImage: `linear-gradient(135deg, #FFFFFF 0%, #FFD6B5 35%, ${ORANGE} 70%, ${ROSE} 100%)`, filter: "drop-shadow(0 4px 28px rgba(251,146,60,0.45))" }}>
        BYO CRUISE
       </span>
       <em className="block italic font-light mt-2 lg:mt-3 text-white/90" style={{ fontSize: "clamp(1.6rem, 4vw, 3rem)" }}>your harbour, your way.</em>
      </h2>
      <p className="text-white/85 text-base sm:text-lg lg:text-xl mt-6 lg:mt-7 max-w-2xl mx-auto leading-[1.65] animate-in fade-in duration-1000" style={{ animationDelay: "300ms" }}>
       Hire the whole Magic. Bring your own food. Bring your own drinks (or use our bar). Sydney Harbour, your way, from <span className="font-bold" style={{ color: ORANGE }}>$2,800</span>.
      </p>
      <div className="mt-9 lg:mt-12 flex flex-col items-center gap-4 animate-in fade-in slide-in-from-bottom-4 duration-1000" style={{ animationDelay: "500ms" }}>
       <div className="flex flex-wrap items-center justify-center gap-3">
        <a href="#packages" className="inline-flex items-center gap-2 px-7 py-4 rounded-full font-black text-xs lg:text-sm tracking-[0.18em] uppercase hover:scale-[1.03] transition-all text-[#0A0A0A]" style={{ background: `linear-gradient(135deg, #FFD6B5 0%, ${ORANGE} 100%)`, boxShadow: "0 20px 50px -10px rgba(251,146,60,0.55)" }} data-testid="byo-hero-packages-cta">
         See packages & pricing <ChevronRight className="w-4 h-4" />
        </a>
        <a href="#enquiry" className="inline-flex items-center gap-2 px-7 py-4 rounded-full font-black text-xs lg:text-sm tracking-[0.18em] uppercase border border-white/30 hover:border-orange-300/70 hover:text-orange-300 transition-all text-white" data-testid="byo-hero-enquiry-cta">
         Send an enquiry
        </a>
       </div>
      </div>
      <div className="mt-10 lg:mt-14 grid grid-cols-3 gap-4 lg:gap-10 max-w-3xl mx-auto animate-in fade-in duration-1000" style={{ animationDelay: "700ms" }}>
       {[
        { i: Anchor, label: ["King St Wharf 3", "Darling Harbour"] },
        { i: Clock, label: ["3.5 to 4 Hour", "Charter"] },
        { i: Users, label: ["Up to 75", "guests"] }].map((c, i) => (
        <div key={i} className="flex flex-col items-center gap-2 lg:gap-3 text-center">
         <span className="w-11 h-11 lg:w-14 lg:h-14 rounded-full flex items-center justify-center ring-1 backdrop-blur" style={{ backgroundColor: "rgba(251,146,60,0.10)", borderColor: ORANGE }}>
          <c.i className="w-4 h-4 lg:w-6 lg:h-6" style={{ color: ORANGE }} strokeWidth={1.5} />
         </span>
         <div className="text-white text-[10px] sm:text-[11px] lg:text-sm uppercase tracking-[0.18em] lg:tracking-[0.22em] font-black leading-snug">
          {c.label.map((line, j) => <div key={j}>{line}</div>)}
         </div>
        </div>
       ))}
      </div>
     </div>
    </div>
   </section>

   {/* HIGHLIGHTS STRIP */}
   <section className="relative border-y border-orange-300/15 bg-gradient-to-r from-[#0F1B2E] via-[#241827] to-[#0F1B2E]" data-testid="byo-highlights">
    <div className="absolute inset-0 opacity-40 pointer-events-none" style={{ background: "radial-gradient(ellipse at 20% 50%, rgba(251,146,60,0.10) 0%, transparent 60%), radial-gradient(ellipse at 80% 50%, rgba(225,29,72,0.08) 0%, transparent 60%)" }} />
    <div className="relative mx-auto max-w-7xl px-5 lg:px-10 py-10 lg:py-14">
     <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-10 lg:gap-x-10">
      {HIGHLIGHTS.map((h, i) => (
       <div key={i} className="flex items-center gap-4 lg:border-r lg:border-orange-300/15 lg:pr-6 last:border-0">
        <div className="w-14 h-14 rounded-full flex items-center justify-center shrink-0 ring-1" style={{ backgroundColor: "rgba(251,146,60,0.10)", borderColor: ORANGE }}>
         <h.icon className="w-6 h-6" style={{ color: ORANGE }} strokeWidth={1.5} />
        </div>
        <div>
         <div className="font-black text-white text-sm tracking-[0.18em]">{h.label}</div>
         <div className="text-white/60 text-[11px] uppercase tracking-[0.22em] font-bold mt-1">{h.sub}</div>
        </div>
       </div>
      ))}
     </div>
    </div>
   </section>

   {/* INTRO */}
   <section className="relative bg-gradient-to-b from-[#0F1B2E] via-[#241827] to-[#0F1B2E] py-24 lg:py-32 overflow-hidden isolate">
    <Starfield density={32} color="#FFD6B5" glow="rgba(251,146,60,0.45)" fixed={false} intensity={0.45} />
    <div className="absolute -top-32 left-1/3 w-[40rem] h-[40rem] rounded-full blur-3xl pointer-events-none" style={{ backgroundColor: "rgba(251,146,60,0.08)" }} />
    <div className="relative z-10 mx-auto max-w-4xl px-5 lg:px-10 text-center">
     <CoralEyebrow>The BYO experience</CoralEyebrow>
     <h2 className="font-black tracking-tight text-white mt-6 leading-[0.95]" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 5vw, 4.5rem)" }}>
      Your event, <em className="font-light italic" style={{ color: ORANGE }}>your rules.</em>
     </h2>
     <p className="text-white/82 text-base lg:text-lg leading-[1.85] mt-7 max-w-3xl mx-auto font-light">
      Magic offers two BYO packages, both are <strong className="text-white">private full-boat charters</strong> on Sydney Harbour. Bring your own caterer (we suggest platters and easy-serve foods), bring your own drinks (Package 1) or use our licensed cash bar (Package 2). Either way, the whole boat is yours: 3 levels, full crew, the harbour as your backdrop.
     </p>
    </div>
   </section>

   {/* PACKAGES */}
   <section id="packages" className="relative bg-gradient-to-b from-[#0F1B2E] via-[#241827] to-[#0F1B2E] py-24 lg:py-32 overflow-hidden isolate border-y border-orange-300/10" data-testid="byo-packages">
    <Starfield density={30} color="#FFD6B5" glow="rgba(251,146,60,0.4)" fixed={false} intensity={0.4} />
    <div className="absolute -top-32 right-1/4 w-[34rem] h-[34rem] rounded-full blur-3xl pointer-events-none" style={{ backgroundColor: "rgba(251,146,60,0.12)" }} />
    <div className="relative z-10 mx-auto max-w-7xl px-5 lg:px-10">
     <div className="text-center max-w-2xl mx-auto mb-14">
      <CoralEyebrow>Two flexible packages</CoralEyebrow>
      <h2 className="font-black tracking-tight text-white mt-6 leading-[0.92]" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 4.5vw, 4rem)" }}>
       Pick the package that <em className="italic font-light" style={{ color: ORANGE }}>fits your vibe.</em>
      </h2>
      <p className="text-white/65 text-base lg:text-lg mt-5 font-light">Both packages are private full-boat charters · pricing per boat (not per person).</p>
     </div>

     {/* Prominent reserve-date CTA banner , sits directly above the tier cards (consistent with EventLanding pages) */}
     <div className="relative rounded-3xl ring-2 mb-10 lg:mb-14 px-6 sm:px-10 py-7 sm:py-9 backdrop-blur-md animate-in fade-in slide-in-from-bottom-2 duration-700"
      style={{ background: `linear-gradient(135deg, ${ORANGE}26 0%, rgba(36,24,39,0.85) 60%, rgba(15,27,46,0.7) 100%)`, borderColor: `${ORANGE}88`, boxShadow: `0 25px 70px -15px ${ORANGE}55, inset 0 0 0 1px ${ORANGE}22` }}
      data-testid="byo-deposit-banner">
      <div className="grid lg:grid-cols-2 gap-7 lg:gap-10 items-start">
       <div>
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.28em] font-black mb-4" style={{ backgroundColor: ORANGE, color: "#0A0A0A" }}>
         <Sparkles className="w-3 h-3" fill="currentColor" strokeWidth={0} /> Secure Your Date
        </div>
        <h3 className="text-white text-2xl sm:text-3xl font-black tracking-tight leading-tight">
         Lock in your BYO cruise date with a <span style={{ color: ORANGE }}>$1,000 deposit</span>
        </h3>
        <p className="text-white/70 text-sm sm:text-base mt-3 leading-relaxed">
         Pay your <strong className="text-white">$1,000 holding deposit</strong> today and we'll lock that date in. It applies as credit toward your final cruise total. Balance due 14 days before the cruise.
        </p>
       </div>

       <div className="w-full">
        <DateAvailabilityChecker cruiseType="byo" cruiseLabel="BYO Cruise" accent="bg-yellow-400 hover:bg-yellow-300 !text-slate-900 shadow-yellow-400/40" testid="byo-date-checker" />
       </div>
      </div>
     </div>

     <div className="grid lg:grid-cols-2 gap-7 lg:gap-9">
      {livePackages.map((p) => (
       <div key={p.id} className={`relative rounded-3xl ring-1 p-8 lg:p-10 backdrop-blur-md ${p.highlight ? "lg:scale-[1.02] z-10" : ""}`} style={{ background: p.highlight ? `linear-gradient(135deg, rgba(251,146,60,0.14), rgba(15,27,46,0.85) 60%, rgba(36,24,39,0.85))` : "rgba(36,24,39,0.55)", borderColor: p.highlight ? `${ORANGE}66` : "rgba(255,255,255,0.12)", boxShadow: p.highlight ? "0 30px 80px -20px rgba(251,146,60,0.40)" : "0 30px 80px -20px rgba(0,0,0,0.4)" }}>
        <div className="flex items-center justify-between mb-6">
         <span className={`inline-flex items-center px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.28em] font-black ${p.highlight ? "" : "ring-1 ring-white/25 bg-white/10 text-white"}`} style={p.highlight ? { backgroundColor: ORANGE, color: "#0A0A0A" } : {}}>{p.badge}</span>
         <Crown className="w-6 h-6" style={{ color: p.highlight ? ORANGE : "rgba(255,255,255,0.5)" }} strokeWidth={1.5} />
        </div>
        <div className="text-white/65 text-[11px] uppercase tracking-[0.32em] font-bold mb-2">{p.tier}</div>
        <h3 className="text-white text-2xl lg:text-3xl font-black mb-4">{p.subtitle}</h3>

        <div className="space-y-2 mb-5">
         {p.pricing.map((s, i) => (
          <div key={i} className="flex items-baseline justify-between border-b border-white/10 pb-2">
           <span className="text-white/72 text-sm uppercase tracking-widest font-bold">{s.season}</span>
           <span className="text-3xl font-black tabular-nums" style={{ color: p.highlight ? ORANGE : "#FFFFFF" }}>{s.price}</span>
          </div>
         ))}
        </div>
        <div className="text-white/55 text-xs uppercase tracking-widest font-bold mb-6">{p.caption}</div>

        <div className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 rounded-full ring-1 text-[11px] uppercase tracking-[0.18em] font-black" style={{ borderColor: `${ORANGE}55`, backgroundColor: `${ORANGE}1A`, color: "#FFD6B5" }}>
         <Users className="w-3.5 h-3.5" /> {p.capacityLabel}
        </div>

        <div className="border-t border-white/10 pt-5 mb-5">
         <div className="text-white/65 text-[10px] uppercase tracking-[0.32em] font-black mb-3">Included in your charter</div>
         <ul className="space-y-2.5">
          {p.inclusions.map((inc, j) => (
           <li key={j} className="flex items-start gap-2.5 text-white/85 text-sm">
            <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: ORANGE }} strokeWidth={2.5} />
            <span>{inc}</span>
           </li>
          ))}
         </ul>
        </div>

        {p.bar && (
         <div className="border-t border-white/10 pt-5 mb-5">
          <div className="text-white/65 text-[10px] uppercase tracking-[0.32em] font-black mb-3 flex items-center gap-2"><GlassWater className="w-3 h-3" /> Bar options</div>
          <ul className="space-y-2.5">
           {p.bar.map((b, j) => (
            <li key={j} className="flex items-start gap-2.5 text-white/85 text-sm">
             <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: ORANGE }} />
             <span>{b}</span>
            </li>
           ))}
          </ul>
         </div>
        )}

        <div className="border-t border-white/10 pt-5 mb-7">
         <div className="text-white/65 text-[10px] uppercase tracking-[0.32em] font-black mb-3 flex items-center gap-2"><AlertCircle className="w-3 h-3" /> Conditions</div>
         <ul className="space-y-2">
          {p.conditions.map((c, j) => (
           <li key={j} className="flex items-start gap-2.5 text-white/65 text-[13px] leading-[1.6]">
            <span className="mt-1.5 w-1 h-1 rounded-full shrink-0 bg-white/40" />
            <span>{c}</span>
           </li>
          ))}
         </ul>
        </div>

        <a href="#enquiry" className={`group flex items-center justify-center gap-2.5 w-full px-6 py-4 rounded-full font-black text-sm tracking-[0.18em] uppercase transition-all ${p.highlight ? "text-[#0A0A0A]" : "text-white ring-1 ring-white/25 hover:ring-orange-300/50 hover:text-orange-300"}`} style={p.highlight ? { background: `linear-gradient(135deg, #FFD6B5 0%, ${ORANGE} 100%)`, boxShadow: "0 15px 40px -10px rgba(251,146,60,0.55)" } : {}} data-testid={`byo-package-cta-${p.id}`}>
         {p.cta} <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
        </a>
       </div>
      ))}
     </div>
    </div>
   </section>

   {/* OPTIONAL EXTRAS */}
   <section className="relative bg-gradient-to-b from-[#0A1424] via-[#0F1830] to-[#0A1424] py-24 lg:py-32 overflow-hidden isolate" data-testid="byo-extras">
    <Starfield density={25} color="#FFD6B5" glow="rgba(251,146,60,0.4)" fixed={false} intensity={0.4} />
    <div className="absolute -top-32 right-1/4 w-[30rem] h-[30rem] rounded-full blur-3xl pointer-events-none" style={{ backgroundColor: "rgba(251,146,60,0.08)" }} />
    <div className="relative z-10 mx-auto max-w-5xl px-5 lg:px-10">
     <div className="text-center max-w-3xl mx-auto mb-12">
      <CoralEyebrow>Add to either package</CoralEyebrow>
      <h2 className="font-black tracking-tight text-white mt-6 leading-[0.92]" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 4.5vw, 4rem)" }}>
       Optional <em className="font-light italic" style={{ color: ORANGE }}>entertainment extras.</em>
      </h2>
      <p className="text-white/65 text-base lg:text-lg mt-5 font-light">Layer on the experience. All bookable as add-ons with us, one invoice, zero extra coordination.</p>
     </div>
     <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
      {EXTRAS.map((e, i) => (
       <div key={i} className="rounded-2xl ring-1 ring-white/10 bg-white/[0.04] p-5 lg:p-6 text-center hover:bg-white/[0.06] hover:ring-orange-300/30 transition-all">
        <e.Icon className="w-7 h-7 mx-auto mb-3" style={{ color: ORANGE }} strokeWidth={1.5} />
        <div className="font-black text-white text-base mb-1.5">{e.label}</div>
        <div className="text-2xl font-black tabular-nums" style={{ color: ORANGE }}>{e.price}</div>
       </div>
      ))}
     </div>
    </div>
   </section>

   {/* REVIEWS */}
   <section className="relative bg-gradient-to-b from-[#0F1B2E] via-[#241827] to-[#0F1B2E] py-24 lg:py-32 border-y border-orange-300/10 overflow-hidden isolate" data-testid="byo-reviews">
    <Starfield density={25} color="#FFD6B5" glow="rgba(251,146,60,0.4)" fixed={false} intensity={0.4} />
    <div className="relative z-10 mx-auto max-w-7xl px-5 lg:px-10">
     <div className="text-center max-w-2xl mx-auto mb-14">
      <CoralEyebrow>Guest reviews · 4.9 / 5</CoralEyebrow>
      <h2 className="font-black tracking-tight text-white mt-6 leading-[0.92]" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 4.5vw, 4rem)" }}>
       BYO done <em className="italic font-light" style={{ color: ORANGE }}>right.</em>
      </h2>
     </div>
     <div className="grid md:grid-cols-3 gap-6">
      {REVIEWS.map((r, i) => (
       <div key={i} className="rounded-2xl ring-1 ring-white/10 bg-white/[0.03] p-6 lg:p-7">
        <div className="flex items-center gap-1 mb-4">{Array.from({ length: r.rating }).map((_, j) => <Star key={j} className="w-4 h-4 fill-current" style={{ color: ORANGE }} />)}</div>
        <p className="text-white/85 text-base leading-[1.7] italic mb-5">"{r.body}"</p>
        <div className="text-white font-black text-sm">{r.name}</div>
        <div className="text-white/55 text-[11px] uppercase tracking-widest font-bold">{r.role}</div>
       </div>
      ))}
     </div>
    </div>
   </section>

   {/* FAQ */}
   <section className="relative bg-gradient-to-b from-[#0F1B2E] via-[#241827] to-[#0F1B2E] py-24 lg:py-32 overflow-hidden isolate" data-testid="byo-faq">
    <Starfield density={18} color="#FFD6B5" glow="rgba(251,146,60,0.35)" fixed={false} intensity={0.35} />
    <div className="relative z-10 mx-auto max-w-3xl px-5 lg:px-10">
     <div className="text-center mb-12"><CoralEyebrow>Frequently asked</CoralEyebrow>
      <h2 className="font-black tracking-tight text-white mt-6 leading-[0.92]" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 4.5vw, 3.5rem)" }}>
       Questions, <em className="italic font-light" style={{ color: ORANGE }}>answered.</em>
      </h2>
     </div>
     <div className="space-y-3">
      {FAQ.map((f, i) => (
       <details key={i} className="group rounded-2xl ring-1 ring-white/10 bg-white/[0.03] p-5 lg:p-6 cursor-pointer">
        <summary className="flex items-center justify-between gap-4 font-black text-white text-base lg:text-lg list-none">
         <span>{f.q}</span>
         <ChevronRight className="w-5 h-5 shrink-0 transition-transform group-open:rotate-90" style={{ color: ORANGE }} />
        </summary>
        <p className="text-white/72 text-base leading-[1.75] mt-4">{f.a}</p>
       </details>
      ))}
     </div>
    </div>
   </section>

   {/* FINAL CTA / ENQUIRY ANCHOR */}
   <section id="enquiry" className="relative overflow-hidden isolate" data-testid="byo-final-cta">
    <img src={MAGIC_PHOTOS.byoSunsetDeck} alt="BYO sunset cruise Sydney" className="absolute inset-0 w-full h-full object-cover scale-110" />
    <div className="absolute inset-0 bg-gradient-to-r from-[#0F1B2E]/95 via-[#241827]/75 to-[#3A1E24]/45" />
    <Starfield density={45} color="#FFD6B5" glow="rgba(251,146,60,0.5)" fixed={false} intensity={0.5} />
    <div className="absolute -top-32 right-1/4 w-[40rem] h-[40rem] rounded-full blur-3xl pointer-events-none animate-pulse" style={{ animationDuration: "6s", backgroundColor: "rgba(251,146,60,0.18)" }} />
    <div className="relative z-10 mx-auto max-w-5xl px-5 lg:px-10 py-24 lg:py-36">
     <div className="text-center">
      <CoralEyebrow>Ready to book?</CoralEyebrow>
      <h2 className="font-black tracking-tight text-white mt-7 leading-[0.92]" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2.25rem, 5.5vw, 5rem)" }}>
       Hire the <em className="italic font-light" style={{ color: ORANGE }}>whole boat.</em>
      </h2>
      <p className="text-white/82 text-base lg:text-lg leading-[1.7] mt-6 max-w-2xl mx-auto">Tell us your date and group size. We'll come back with a tailored BYO package within 24 hours.</p>
      <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
       <Link to="/contact" className="group inline-flex items-center gap-2.5 px-8 py-4 rounded-full font-black text-sm tracking-[0.18em] uppercase hover:scale-[1.03] transition-all text-[#0A0A0A]" style={{ background: `linear-gradient(135deg, #FFD6B5 0%, ${ORANGE} 100%)`, boxShadow: "0 20px 50px -10px rgba(251,146,60,0.55)" }} data-testid="byo-final-enquiry">
        Send an enquiry <ArrowRight className="w-4 h-4" />
       </Link>
       <a href={`tel:${CONTACT.phone1}`} className="group inline-flex items-center gap-2.5 text-white/85 hover:text-white px-6 py-4 rounded-full font-black text-sm tracking-[0.18em] uppercase border border-white/15 hover:border-white/40 backdrop-blur transition-all">
        <Phone className="w-4 h-4" /> {CONTACT.phone1}
       </a>
      </div>
     </div>
    </div>
   </section>
  </main>
 );
}

function CoralEyebrow({ children }) {
 return (
  <div className="inline-flex items-center gap-3 text-[10px] uppercase tracking-[0.42em] font-black">
   <span className="block w-6 h-px" style={{ backgroundColor: `${ORANGE}99` }} />
   <span style={{ color: ORANGE }}>{children}</span>
   <span className="block w-6 h-px" style={{ backgroundColor: `${ORANGE}99` }} />
  </div>
 );
}
